import { toast } from 'react-toastify';

export function isStandalone(): boolean {
  // iOS Safari standalone
  // @ts-ignore
  if (typeof window !== 'undefined' && window.navigator && window.navigator.standalone) return true;
  // All browsers supporting display-mode
  return typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches || false;
}

type VersionPayload = {
  version: string;
  builtAt?: string;
  forceLogout?: boolean;
  redirectTo?: string;
  message?: string;
};

async function fetchVersion(): Promise<VersionPayload | null> {
  try {
    const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) ? process.env.PUBLIC_URL : '';
    // Add cache-busting param to avoid proxy/CDN stale caching
    const res = await fetch(`${base}/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function clearAllCaches() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // ignore errors
  }
}

function clearAuthAndRedirect() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('forcePasswordChange');
  } catch {}
  // Send users to homepage after logout
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
}

export async function initPwaUpdateCheck(intervalMinutes = 60) {
  // Only run in production to avoid noisy reloads during dev
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    return;
  }

  // Tab coordination (only one tab triggers heavy work)
  const channel = (typeof window !== 'undefined' && 'BroadcastChannel' in window) ? new BroadcastChannel('app-updates') : null;
  if (channel) {
    channel.onmessage = (e: MessageEvent) => {
      if (e?.data?.type === 'version-updated') {
        const force = e.data?.payload?.forceLogout !== undefined ? e.data.payload.forceLogout : true;
        const target = e.data?.payload?.redirectTo || '/';
        const message = e.data?.payload?.message as string | undefined;
        if (force) {
          clearAuthAndRedirectWithTarget(target);
        } else {
          notifySoftUpdate({ version: e.data?.payload?.version, redirectTo: target, message, forceLogout: false });
        }
      }
    };
  } else if (typeof window !== 'undefined') {
    // Fallback via storage events
    window.addEventListener('storage', (ev) => {
      if (ev.key === 'app_version_updated' && ev.newValue) {
        try {
          const parsed = JSON.parse(ev.newValue);
          const force = parsed?.forceLogout !== undefined ? parsed.forceLogout : true;
          const target = parsed?.redirectTo || '/';
          const message = parsed?.message as string | undefined;
          if (force) {
            clearAuthAndRedirectWithTarget(target);
          } else {
            notifySoftUpdate({ version: parsed?.version, redirectTo: target, message, forceLogout: false });
          }
        } catch {
          clearAuthAndRedirectWithTarget();
        }
      }
    });
  }

  const storageKey = 'app_version';
  const current = await fetchVersion();
  if (!current) return;

  const stored = localStorage.getItem(storageKey);
  if (stored !== current.version) {
    if (isStandalone()) {
      await clearAllCaches();
    }
    localStorage.setItem(storageKey, current.version);
    // broadcast to other tabs
    channel?.postMessage({ type: 'version-updated', payload: { version: current.version, redirectTo: current.redirectTo, forceLogout: current.forceLogout, message: current.message } });
    try { localStorage.setItem('app_version_updated', JSON.stringify({ ts: Date.now(), version: current.version, redirectTo: current.redirectTo, forceLogout: current.forceLogout, message: current.message })); } catch {}
    const force = current.forceLogout !== undefined ? current.forceLogout : true;
    const target = current.redirectTo || '/';
    if (force) {
      clearAuthAndRedirectWithTarget(target);
    } else {
      notifySoftUpdate({ version: current.version, redirectTo: target, message: current.message, forceLogout: false });
    }
    return;
  }

  setInterval(async () => {
    const latest = await fetchVersion();
    if (!latest) return;
    const v = localStorage.getItem(storageKey);
    if (v !== latest.version) {
      if (isStandalone()) {
        await clearAllCaches();
      }
      localStorage.setItem(storageKey, latest.version);
      channel?.postMessage({ type: 'version-updated', payload: { version: latest.version, redirectTo: latest.redirectTo, forceLogout: latest.forceLogout, message: latest.message } });
      try { localStorage.setItem('app_version_updated', JSON.stringify({ ts: Date.now(), version: latest.version, redirectTo: latest.redirectTo, forceLogout: latest.forceLogout, message: latest.message })); } catch {}
      const force = latest.forceLogout !== undefined ? latest.forceLogout : true;
      const target = latest.redirectTo || '/';
      if (force) {
        clearAuthAndRedirectWithTarget(target);
      } else {
        notifySoftUpdate({ version: latest.version, redirectTo: target, message: latest.message, forceLogout: false });
      }
    }
  }, intervalMinutes * 60 * 1000);
}

function clearAuthAndRedirectWithTarget(target?: string) {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('forcePasswordChange');
  } catch {}
  if (typeof window !== 'undefined') {
    window.location.href = target || '/';
  }
}

function notifySoftUpdate(payload: { version?: string; redirectTo?: string; message?: string; forceLogout?: boolean }) {
  // Persist payload for components to pick up
  try { localStorage.setItem('app_update_payload', JSON.stringify(payload)); } catch {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('app_update_available', { detail: payload }));
  }
}
